// Used-gun intake (W9/N2). Real-world flow: seller first, then identify,
// then physical inspection, then photos for records, then offer, then
// prep.

import { useState } from "react";
import { AiIdentifyStep, type AiSuggestions } from "./AiIdentifyStep";
import { type ConditionState, ConditionStep } from "./ConditionStep";
import { Stepper, StepTitle } from "./components";
import { InfoRow as SellerInfoRow } from "./FirearmSaleParts";
import { Check, MockShell, TouchButton } from "./MockShell";
import { ReceiveStep, ValuationStep } from "./UsedGunIntakeParts";

type StepId = "seller" | "identify" | "condition" | "photos" | "valuation" | "receive";

const steps: { id: StepId; label: string }[] = [
  { id: "seller", label: "Seller" },
  { id: "identify", label: "Identify" },
  { id: "condition", label: "Condition" },
  { id: "photos", label: "Photos" },
  { id: "valuation", label: "Offer" },
  { id: "receive", label: "Receive" },
];

const titles: Record<StepId, string> = {
  seller: "Seller",
  identify: "Identify the firearm",
  condition: "Condition",
  photos: "Photos",
  valuation: "Make an offer",
  receive: "Receive",
};

const photoSlots = ["Left", "Right", "Top (roll marks)", "Serial close-up", "Bore", "Accessories"];

const intakeOptions = [
  { id: "buyback", label: "Buyback", sub: "We pay in cash or check" },
  { id: "trade-in", label: "Trade-in", sub: "Credit toward a purchase" },
  { id: "evaluate", label: "Evaluate", sub: "Appraise only, no offer" },
] as const;

type IntakeType = (typeof intakeOptions)[number]["id"];

export function UsedGunIntake() {
  const [current, setCurrent] = useState<StepId>("seller");
  const [intakeType, setIntakeType] = useState<IntakeType>("buyback");
  const [photos, setPhotos] = useState<Record<string, boolean>>({});
  const [ai, setAi] = useState<AiSuggestions>({
    make: { label: "Make", suggested: "Smith & Wesson", confidence: 0.96, value: "Smith & Wesson" },
    model: { label: "Model", suggested: "Model 686-6", confidence: 0.82, value: "Model 686-6" },
    caliber: { label: "Caliber", suggested: ".357 Magnum", confidence: 0.91, value: ".357 Magnum" },
    type: { label: "Type", suggested: "Revolver", confidence: 0.99, value: "Revolver" },
    barrel: { label: "Barrel", suggested: '4"', confidence: 0.74, value: '4"' },
    finish: { label: "Finish", suggested: "Stainless", confidence: 0.88, value: "Stainless" },
  });
  const [serial, setSerial] = useState("CBW4421");
  const [isPmf, setIsPmf] = useState(false);
  const [condition, setCondition] = useState<ConditionState>({
    finishPct: 90,
    bore: "clean",
    functionCheck: true,
    aftermarket: false,
    missingParts: false,
    notes: "",
  });
  const [offerCents, setOfferCents] = useState(42500);
  const [accepted, setAccepted] = useState(false);
  const suggestedRetailCents = 69900;
  const targetMarginPct = 40;

  const currentIdx = steps.findIndex((s) => s.id === current);
  const next = () => {
    const n = steps[currentIdx + 1];
    if (n) setCurrent(n.id);
  };
  const prev = () => {
    const p = steps[currentIdx - 1];
    if (p) setCurrent(p.id);
  };

  const photosDone = photoSlots.filter((p) => photos[p]).length;

  const activeIntake = intakeOptions.find((o) => o.id === intakeType) ?? intakeOptions[0];
  if (!activeIntake) throw new Error("intakeOptions must not be empty");

  return (
    <MockShell title="Used-gun intake" signedInAs="Sarah T." device="Floor iPad 1">
      <div className="max-w-2xl mx-auto w-full pb-10">
        <div className="px-4 sm:px-6 pb-2">
          <div className="inline-flex gap-1 p-1 rounded-full bg-slate-200/70 w-full">
            {intakeOptions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setIntakeType(opt.id)}
                className={`flex-1 min-h-9 rounded-full text-[14px] font-medium transition ${
                  intakeType === opt.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="text-[12px] text-slate-500 mt-1.5 px-1">{activeIntake.sub}</div>
        </div>
        <Stepper steps={steps} current={current} onPick={setCurrent} />
        <StepTitle>{titles[current]}</StepTitle>

        <div className="px-4 sm:px-6">
          {current === "seller" && (
            <>
              <div className="bg-white rounded-2xl p-5">
                <div className="flex items-center gap-4">
                  <span className="h-14 w-14 rounded-full bg-slate-900 text-white grid place-items-center text-[17px] font-semibold shrink-0">
                    FN
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[22px] font-semibold tracking-tight break-words">
                      Fake Name Four
                    </div>
                    <div className="text-[13px] text-slate-500">Age 47 · Plymouth, MN</div>
                  </div>
                </div>
              </div>

              <div className="mt-4 bg-white rounded-2xl overflow-hidden">
                <SellerInfoRow label="Phone" value="(763) 555-0404" />
                <SellerInfoRow label="Email" value="four@example.test" />
                <SellerInfoRow label="Address" value="404 Test Ln, Plymouth, MN 55441" />
                <SellerInfoRow
                  label="License"
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      MN · scanned just now
                      <Check className="text-emerald-500" />
                    </span>
                  }
                />
                <SellerInfoRow label="Prior sales to us" value="2 (both 2025)" />
              </div>

              <div className="mt-6">
                <TouchButton size="lg" full onClick={next}>
                  Continue
                </TouchButton>
              </div>
            </>
          )}

          {current === "identify" && (
            <AiIdentifyStep
              serial={serial}
              setSerial={setSerial}
              ai={ai}
              setAi={setAi}
              isPmf={isPmf}
              setIsPmf={setIsPmf}
              onPrev={prev}
              onNext={next}
            />
          )}

          {current === "condition" && (
            <ConditionStep
              condition={condition}
              setCondition={setCondition}
              onPrev={prev}
              onNext={next}
            />
          )}

          {current === "photos" && (
            <>
              <p className="text-[14px] text-slate-500 mb-3 px-1">
                For our records and the resale listing. Four minimum.
              </p>
              <div className="text-[13px] text-slate-500 mb-2 px-1 tabular-nums">
                {photosDone} of {photoSlots.length}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {photoSlots.map((slot) => {
                  const done = photos[slot];
                  return (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => setPhotos({ ...photos, [slot]: !done })}
                      className={`aspect-square rounded-2xl p-3 flex flex-col items-start justify-between text-left transition ${
                        done
                          ? "bg-emerald-50"
                          : "bg-white border border-dashed border-slate-300 active:bg-slate-50"
                      }`}
                    >
                      <span className="text-[12px] font-medium text-slate-700">{slot}</span>
                      <span className={`text-2xl ${done ? "text-emerald-500" : "text-slate-300"}`}>
                        {done ? "✓" : "+"}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-6">
                <TouchButton size="lg" full onClick={next} disabled={photosDone < 4}>
                  Continue
                </TouchButton>
              </div>
            </>
          )}

          {current === "valuation" && (
            <ValuationStep
              offerCents={offerCents}
              setOfferCents={setOfferCents}
              suggestedRetailCents={suggestedRetailCents}
              targetMarginPct={targetMarginPct}
              accepted={accepted}
              setAccepted={setAccepted}
              onNext={next}
            />
          )}

          {current === "receive" && <ReceiveStep intakeType={intakeType} />}
        </div>
      </div>
    </MockShell>
  );
}
